/**
 * Border and corner resize handles of a frame window drawn by
 * `FrameWindowShell`: the window shows the resized box while the handle
 * moves and commits it when the pointer is released.
 */
import {
  useEffect,
  useRef,
  type CSSProperties,
  type PointerEvent,
} from "react";
import {
  RESIZE_CORNERS,
  RESIZE_LINES,
  resizeCornerStyle,
  resizeCursor,
  resizeLineStyle,
  type ResizePosition,
} from "@/components/focus/frame-resize-geometry";
import type { FrameControlsProps } from "@/components/shapes/shared/frame-renderer-context";
import {
  beginNativeGlassGesture,
  endNativeGlassGesture,
} from "@/desktop/native-glass-gesture";
import { cn } from "@/lib/cn";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import {
  FRAME_MAX_HEIGHT,
  frameMinHeight,
  frameMinWidth,
} from "@/lib/frame-window-limits";
import { useFrameWindow } from "./frame-window-context";

// The top line sits mostly above the paper: the top of the paper is the
// window's drag band (`frame-window-band.ts`).
const HANDLE_PLACEMENT: Record<ResizePosition, CSSProperties> = {
  top: { top: 0, transform: "translateY(-75%)" },
  bottom: { bottom: 0, transform: "translateY(50%)" },
  left: { left: 0, transform: "translateX(-50%)" },
  right: { right: 0, transform: "translateX(50%)" },
  "top-left": { top: 0, left: 0, transform: "translate(-50%, -50%)" },
  "top-right": { top: 0, right: 0, transform: "translate(50%, -50%)" },
  "bottom-left": { bottom: 0, left: 0, transform: "translate(-50%, 50%)" },
  "bottom-right": { bottom: 0, right: 0, transform: "translate(50%, 50%)" },
};

/** `start` resized from `position` by the pointer travel, within limits. */
function resizedFrameWindowBox(
  position: ResizePosition,
  start: FocusWindowBox,
  travel: { dx: number; dy: number },
  frameType: string,
): FocusWindowBox {
  const fromLeft = position.includes("left");
  const fromTop = position.includes("top");
  const horizontal = fromLeft || position.includes("right");
  const vertical = fromTop || position.includes("bottom");
  const width = horizontal
    ? Math.max(
        frameMinWidth(frameType),
        start.width + (fromLeft ? -travel.dx : travel.dx),
      )
    : start.width;
  const height = vertical
    ? Math.min(
        FRAME_MAX_HEIGHT,
        Math.max(
          frameMinHeight(frameType),
          start.height + (fromTop ? -travel.dy : travel.dy),
        ),
      )
    : start.height;
  return {
    x: fromLeft ? start.x + start.width - width : start.x,
    y: fromTop ? start.y + start.height - height : start.y,
    width,
    height,
  };
}

export function FrameWindowResizeHandles({
  frameId,
  frameType,
  visible,
}: FrameControlsProps) {
  const frameWindow = useFrameWindow();
  // Ends a resize whose handles unmount before the pointer is released.
  const abortRef = useRef<(() => void) | null>(null);
  useEffect(() => () => abortRef.current?.(), []);
  if (!visible || !frameWindow) return null;
  const { box, showBox, commitResize, testIdPrefix } = frameWindow;

  const handlePointerDown = (
    position: ResizePosition,
    event: PointerEvent<HTMLDivElement>,
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    abortRef.current?.();
    const start = box;
    const press = { x: event.clientX, y: event.clientY };
    let last: FocusWindowBox | null = null;
    beginNativeGlassGesture("window-drag");
    function onMove(e: globalThis.PointerEvent) {
      beginNativeGlassGesture("window-drag");
      last = resizedFrameWindowBox(
        position,
        start,
        { dx: e.clientX - press.x, dy: e.clientY - press.y },
        frameType,
      );
      showBox(last);
    }
    function stop() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      endNativeGlassGesture("window-drag");
      abortRef.current = null;
    }
    function onUp() {
      stop();
      if (last) commitResize(last);
      showBox(null);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    abortRef.current = stop;
  };

  const handle = (position: ResizePosition, style: CSSProperties) => (
    <div
      key={position}
      className={cn(
        "denker-frame-resize-control nodrag nowheel absolute z-[70] touch-none select-none",
        resizeCursor(position),
      )}
      style={{ ...style, ...HANDLE_PLACEMENT[position] }}
      data-resize-position={position}
      data-testid={`${testIdPrefix}-resize-${position}-${frameId}`}
      aria-hidden="true"
      onPointerDown={(event) => handlePointerDown(position, event)}
    />
  );
  return (
    <>
      {RESIZE_LINES.map((position) =>
        handle(position, resizeLineStyle(position)),
      )}
      {RESIZE_CORNERS.map((position) =>
        handle(position, resizeCornerStyle(position)),
      )}
    </>
  );
}
